import { useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, formatGbp, formatPct, formatQuantity, formatRelative, formatTime } from '../api';
import { usePolling } from '../auth';
import {
  Empty,
  Figure,
  KindTag,
  Meter,
  Money,
  Note,
  Panel,
  Percent,
  Spinner,
  StreamName,
  Tag,
} from '../components/ui';
import { SignificanceView } from './Backtest';
import type {
  BenchmarkSummary,
  DashboardResponse,
  DashboardStream,
  EquityResponse,
  RunCycleResponse,
  SignificanceReport,
} from '../types';

export default function Dashboard({
  pushToast,
}: {
  pushToast: (message: string, tone?: 'success' | 'error') => void;
}) {
  const { data, error, loading, reload } = usePolling<DashboardResponse>(() => api.dashboard(), 30_000);
  const equity = usePolling<EquityResponse>(() => api.equity(), 60_000);
  const significance = usePolling<{ report: SignificanceReport }>(() => api.liveSignificance(), 120_000);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState<RunCycleResponse | null>(null);

  async function runCycle(force: boolean): Promise<void> {
    setRunning(true);
    setLastRun(null);
    try {
      const result = await api.runCycle({ force });
      setLastRun(result);
      const executed = result.results.reduce((sum, entry) => sum + entry.ordersExecuted, 0);
      pushToast(
        result.skippedReason
          ? result.skippedReason
          : `Cycle complete — ${executed} fill(s) across ${result.results.length} stream(s).`,
        'success',
      );
      reload();
      equity.reload();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Cycle failed', 'error');
    } finally {
      setRunning(false);
    }
  }

  if (loading && !data) {
    return (
      <div className="sheet">
        <Spinner label="loading desk" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="sheet">
        <Note tone="error" title="Desk unavailable">
          {error}
        </Note>
      </div>
    );
  }
  if (!data) return null;

  const marketsClosed = !data.sessions.LSE.isOpen && !data.sessions.US.isOpen;
  const leader = data.leaderboard[0];
  const streamCount = data.streams.length;
  const countWord =
    ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'][streamCount] ??
    String(streamCount);

  return (
    <>
      {/* Editorial masthead: a heavy title against a live tape of the totals. */}
      <header className="masthead">
        <div className="masthead-title">
          <div className="eyebrow">{countWord} strategies · one universe · {data.maxTradesPerDay} trades/day each</div>
          <h1 style={{ marginTop: 10 }}>The Arena</h1>
          <p className="masthead-kicker">
            Groq AI, mechanical pattern matching, a consensus of both, 12-1 momentum and a
            cash-secured SPY put-write — each funded equally, every order checked against the latest
            news, each judged on what it returns above the market rather than in absolute terms.
          </p>
        </div>
        <div className="tape">
          <div className="tape-cell">
            <div className="tape-k">Equity</div>
            <div className="tape-v">{formatGbp(data.totals.equityGbp)}</div>
          </div>
          <div className="tape-cell">
            <div className="tape-k">Combined P&amp;L</div>
            <div className={`tape-v ${data.totals.totalPnlGbp > 0 ? 'up' : data.totals.totalPnlGbp < 0 ? 'down' : ''}`}>
              {data.totals.totalPnlGbp >= 0 ? '+' : ''}
              {formatGbp(data.totals.totalPnlGbp)}
            </div>
          </div>
          <div className="tape-cell">
            <div className="tape-k">Open</div>
            <div className="tape-v">{data.totals.openPositions}</div>
          </div>
          <div className="tape-cell">
            <div className="tape-k">Today</div>
            <div className="tape-v">{data.totals.tradesToday}</div>
          </div>
        </div>
      </header>

      <div className="sheet">
        <div className="keys" style={{ justifyContent: 'flex-end' }}>
          <button className="key" type="button" onClick={reload} disabled={running}>
            refresh
          </button>
          <button
            className="key key-primary"
            type="button"
            onClick={() => void runCycle(marketsClosed)}
            disabled={running}
          >
            {running ? <Spinner label="running" /> : marketsClosed ? 'force cycle' : 'run cycle'}
          </button>
        </div>

        {data.regime && (
          <Note
            tone={data.regime.regime === 'bear' ? 'warn' : 'info'}
            title={`Market regime — ${data.regime.regime.toUpperCase()}`}
          >
            {data.regime.summary}{' '}
            {(data.regime.breadthUp * 100).toFixed(0)}% of the universe is trending up
            {data.regime.meanDistanceFrom200 !== null &&
              `, averaging ${data.regime.meanDistanceFrom200 >= 0 ? '+' : ''}${data.regime.meanDistanceFrom200.toFixed(1)}% against the 200-day.`}
          </Note>
        )}

        {!data.groqConfigured && (
          <Note tone="warn" title="No Groq key — AI streams idle">
            The Groq AI and AI+Pattern streams cannot decide without one. Set <code>GROQ_API_KEY</code>{' '}
            and restart; the mechanical Pattern stream runs regardless.
          </Note>
        )}

        {marketsClosed && (
          <Note tone="info" title="Exchanges shut">
            The scheduler stands down out of hours. A forced cycle exercises the strategies against
            the most recent close.
          </Note>
        )}

        {data.streams.some((stream) => stream.halted) && (
          <Note tone="warn" title="Stream halted">
            {data.streams
              .filter((stream) => stream.halted)
              .map((stream) => `${stream.name}: ${stream.haltReason ?? 'no reason recorded'}`)
              .join(' · ')}
            . Resume from Streams (F2).
          </Note>
        )}

        {/* Off-centre split: the ranked table dominates, figures stack beside it. */}
        <div className="split-73">
          <Panel title="Leaderboard" note="ranked by alpha over the market">
            <div className="grid-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>#</th>
                    <th>Stream</th>
                    <th>Engine</th>
                    <th className="num">Return</th>
                    <th className="num">Mkt</th>
                    <th className="num">Alpha</th>
                    <th className="num">P&amp;L</th>
                    <th className="num">Win</th>
                    <th className="num">PF</th>
                    <th className="num">Sharpe</th>
                    <th className="num">Trades</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leaderboard.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        <span className="rank">{entry.rank}</span>
                      </td>
                      <td>
                        <StreamName name={entry.name} colour={entry.colour} />
                        <div className="mini">{entry.model ?? 'deterministic'}</div>
                      </td>
                      <td>
                        <KindTag kind={entry.kind} />
                      </td>
                      <td className="num">
                        <Percent value={entry.totalReturnPct} decimals={2} />
                      </td>
                      <td className="num flat">
                        {entry.benchmarkReturnPct === null ? '—' : formatPct(entry.benchmarkReturnPct, 1)}
                      </td>
                      <td className="num">
                        {entry.alphaPct === null ? (
                          <span className="flat">—</span>
                        ) : (
                          <>
                            <Percent value={entry.alphaPct} decimals={2} />
                            <div className="mini">{entry.beatsBenchmark ? 'ahead of mkt' : 'behind mkt'}</div>
                          </>
                        )}
                      </td>
                      <td className="num">
                        <Money value={entry.totalPnlGbp} showSign />
                      </td>
                      <td className="num">
                        {entry.winRatePct === null ? '—' : `${entry.winRatePct.toFixed(0)}%`}
                      </td>
                      <td className="num">
                        {entry.profitFactor === null ? '—' : entry.profitFactor.toFixed(2)}
                      </td>
                      <td className="num">
                        {entry.sharpeRatio === null ? '—' : entry.sharpeRatio.toFixed(2)}
                      </td>
                      <td className="num">
                        {entry.totalTrades}
                        {entry.closedTrades > 0 && <span className="flat"> · {entry.closedTrades}c</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="prose" style={{ marginTop: 12 }}>
              Alpha is the verdict. A stream up 2% while the market ran 6% has lost you money in real
              terms. Win rate, profit factor and Sharpe only firm up once each stream has closed a
              meaningful number of positions.
            </p>
          </Panel>

          <div className="cluster" style={{ gap: 12 }}>
            <Figure
              label="Deposited"
              value={formatGbp(data.totals.depositedGbp)}
              foot={`across ${data.streams.length} streams`}
            />
            <Figure
              label="Costs paid"
              value={formatGbp(data.totals.feesPaidGbp)}
              foot="simulated dealing costs"
            />
            {leader && (
              <Figure
                label="Front runner"
                value={leader.name}
                foot={
                  leader.alphaPct === null
                    ? 'no benchmark yet'
                    : `${leader.alphaPct >= 0 ? '+' : ''}${leader.alphaPct.toFixed(2)}% alpha`
                }
                tone={leader.alphaPct === null ? 'flat' : leader.alphaPct > 0 ? 'up' : 'down'}
              />
            )}
          </div>
        </div>

        {data.benchmark && <BenchmarkPanel benchmark={data.benchmark} />}

        <Panel title="Equity curves" note="marked to market each cycle">
          {equity.data && equity.data.merged.length > 1 ? (
            <>
              <div className="chart-frame">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={equity.data.merged} margin={{ top: 6, right: 14, bottom: 4, left: 4 }}>
                    <CartesianGrid stroke="#2a2c22" strokeDasharray="2 4" />
                    <XAxis
                      dataKey="at"
                      tick={{ fill: '#6a6a58', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      stroke="#3d4032"
                      tickFormatter={(value: string) => formatTime(value)}
                      minTickGap={44}
                    />
                    <YAxis
                      tick={{ fill: '#6a6a58', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      stroke="#3d4032"
                      domain={['auto', 'auto']}
                      tickFormatter={(value: number) => `£${Math.round(value).toLocaleString('en-GB')}`}
                      width={70}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#1a1c16',
                        border: '1px solid #3d4032',
                        borderRadius: 0,
                        fontSize: 12,
                        fontFamily: 'IBM Plex Mono',
                      }}
                      labelFormatter={(label) =>
                        typeof label === 'string' ? new Date(label).toLocaleString('en-GB') : ''
                      }
                      formatter={(value) => (typeof value === 'number' ? formatGbp(value) : String(value))}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} />
                    {equity.data.series.map((series) => (
                      <Line
                        key={series.id}
                        type="monotone"
                        dataKey={series.name}
                        stroke={series.colour}
                        strokeWidth={1.75}
                        dot={false}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="prose" style={{ marginTop: 10 }}>
                Every stream starts from the same deposit, so the curves begin level and diverge
                purely on decision quality.
              </p>
            </>
          ) : (
            <Empty>Equity is recorded after each cycle — run a couple to draw the curves.</Empty>
          )}
        </Panel>

        {significance.data && <SignificanceView report={significance.data.report} />}
        {lastRun && <CycleReport run={lastRun} />}

        {/* Deliberately staggered: two per row, framed panels, not an even stack. */}
        <div className="cluster cluster-2">
          {data.streams.map((stream) => (
            <StreamPanel key={stream.id} stream={stream} />
          ))}
        </div>
      </div>
    </>
  );
}

function BenchmarkPanel({ benchmark }: { benchmark: BenchmarkSummary }) {
  return (
    <Panel
      title="The market"
      note={
        benchmark.fromDate
          ? `equal-weight buy & hold · ${benchmark.fromDate} → ${benchmark.toDate ?? 'now'}`
          : 'insufficient history'
      }
      actions={
        benchmark.reliable ? (
          <Tag mark="✓">{benchmark.constituentCount} names</Tag>
        ) : (
          <Tag tone="warn" mark="!">thin coverage</Tag>
        )
      }
    >
      {benchmark.returnPct === null ? (
        <Empty>{benchmark.note}</Empty>
      ) : (
        <div className="split-64">
          <div className="cluster cluster-2">
            <Figure
              label="Market return"
              value={formatPct(benchmark.returnPct, 2)}
              tone={benchmark.returnPct >= 0 ? 'up' : 'down'}
              foot="the bar to beat"
            />
            <Figure
              label="Coverage"
              value={`${(benchmark.coverage * 100).toFixed(0)}%`}
              foot={`${benchmark.constituentCount} priced · ${benchmark.missingCount} dark`}
            />
          </div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Best & worst</div>
            <dl className="kv">
              {benchmark.best.slice(0, 3).map((entry) => (
                <div key={entry.symbol} style={{ display: 'contents' }}>
                  <dt>{entry.symbol}</dt>
                  <dd className="up">{formatPct(entry.returnPct, 1)}</dd>
                </div>
              ))}
              {benchmark.worst.slice(0, 2).map((entry) => (
                <div key={entry.symbol} style={{ display: 'contents' }}>
                  <dt>{entry.symbol}</dt>
                  <dd className="down">{formatPct(entry.returnPct, 1)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </Panel>
  );
}

function CycleReport({ run }: { run: RunCycleResponse }) {
  return (
    <Panel
      title="Cycle log"
      note={`${run.snapshotUsable}/${run.symbolsConsidered} instruments with usable data`}
    >
      {run.skippedReason && <Note tone="info">{run.skippedReason}</Note>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {run.results.map((result) => (
          <div key={result.streamId} style={{ borderLeft: '2px solid var(--hair-strong)', paddingLeft: 14 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontFamily: 'var(--display)' }}>{result.streamName}</strong>
              <Tag
                tone={result.status === 'ok' ? 'up' : result.status === 'error' ? 'down' : 'neutral'}
              >
                {result.status}
              </Tag>
              <span className="mini">
                {result.ordersProposed} proposed · {result.ordersExecuted} executed ·{' '}
                {Math.round(result.durationMs)}ms
              </span>
            </div>
            <p className="reason" style={{ margin: '5px 0 0' }}>
              {result.note}
            </p>
            {result.fills.length > 0 && (
              <ul style={{ margin: '7px 0 0', paddingLeft: 16, fontSize: 12, fontFamily: 'var(--mono)' }}>
                {result.fills.map((fill, index) => (
                  <li key={`${fill.symbol}-${index}`} className={fill.status === 'filled' ? 'up' : 'flat'}>
                    {fill.status === 'filled'
                      ? fill.summary
                      : `${fill.symbol} ${fill.action} blocked — ${fill.reason}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      {run.snapshotFailures.length > 0 && (
        <details className="fold" style={{ marginTop: 14 }}>
          <summary>{run.snapshotFailures.length} instrument(s) skipped for missing data</summary>
          <pre>
            {run.snapshotFailures.map((failure) => `${failure.symbol}: ${failure.reason}`).join('\n')}
          </pre>
        </details>
      )}
    </Panel>
  );
}

function StreamPanel({ stream }: { stream: DashboardStream }) {
  const tone = stream.totalPnlGbp > 0 ? 'up' : stream.totalPnlGbp < 0 ? 'down' : 'flat';

  return (
    <Panel
      title={<StreamName name={stream.name} colour={stream.colour} />}
      actions={
        <>
          <KindTag kind={stream.kind} />
          {!stream.enabled && <Tag>off</Tag>}
          {stream.halted && <Tag tone="down" mark="■">halted</Tag>}
          {stream.anyStalePrices && <Tag tone="warn" mark="!">stale</Tag>}
        </>
      }
    >
      {stream.model && <div className="mini" style={{ marginBottom: 12 }}>model · {stream.model}</div>}

      <div className="cluster cluster-3" style={{ marginBottom: 14 }}>
        <Figure label="Equity" value={formatGbp(stream.equityGbp)} />
        <Figure
          label="Total P&L"
          value={
            <>
              {stream.totalPnlGbp >= 0 ? '+' : ''}
              {formatGbp(stream.totalPnlGbp)}
            </>
          }
          tone={tone}
          foot={formatPct(stream.totalReturnPct, 2)}
        />
        <Figure
          label="Cash"
          value={formatGbp(stream.cashGbp)}
          foot={
            stream.equityGbp > 0 ? `${((stream.cashGbp / stream.equityGbp) * 100).toFixed(0)}% dry` : ''
          }
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span className="eyebrow">Daily allowance</span>
          <span className="mini">
            {stream.dailyLimit.used} / {stream.dailyLimit.limit}
          </span>
        </div>
        <Meter used={stream.dailyLimit.used} limit={stream.dailyLimit.limit} colour={stream.colour} />
      </div>

      <dl className="kv" style={{ marginBottom: 14 }}>
        <dt>Realised</dt>
        <dd>
          <Money value={stream.realisedPnlGbp} showSign />
        </dd>
        <dt>Unrealised</dt>
        <dd>
          <Money value={stream.unrealisedPnlGbp} showSign />
        </dd>
        <dt>Costs</dt>
        <dd>{formatGbp(stream.feesPaidGbp)}</dd>
        <dt>Drawdown</dt>
        <dd>
          <Percent value={stream.drawdownPct} />
        </dd>
        <dt>Exposure</dt>
        <dd>
          {formatGbp(stream.longExposureGbp)} long
          {stream.shortExposureGbp > 0 && ` / ${formatGbp(stream.shortExposureGbp)} short`}
          <span className="flat"> · {stream.grossExposurePct.toFixed(0)}% gross</span>
        </dd>
        <dt>Win rate</dt>
        <dd>
          {stream.stats.winRatePct === null
            ? '— no closed trades'
            : `${stream.stats.winRatePct.toFixed(0)}% of ${stream.stats.closedTrades}`}
        </dd>
        <dt>vs market</dt>
        <dd>
          {stream.alphaPct === null ? (
            '— no benchmark'
          ) : (
            <>
              <Percent value={stream.alphaPct} decimals={2} />
              <span className="flat"> (mkt {formatPct(stream.benchmarkReturnPct, 1)})</span>
            </>
          )}
        </dd>
      </dl>

      <div className="eyebrow" style={{ marginBottom: 8 }}>
        Open positions · {stream.positions.length}
      </div>
      {stream.positions.length === 0 ? (
        <Empty>Flat. No open positions.</Empty>
      ) : (
        <div className="grid-wrap">
          <table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th className="num">Qty</th>
                <th className="num">Avg</th>
                <th className="num">Now</th>
                <th className="num">Value</th>
                <th className="num">P&amp;L</th>
                <th className="num">Wt</th>
              </tr>
            </thead>
            <tbody>
              {stream.positions.map((position) => (
                <tr key={position.symbol}>
                  <td>
                    <span className="sym">{position.symbol}</span>
                    {position.direction === 'SHORT' && (
                      <>
                        {' '}
                        <Tag tone="down" mark="▼">S</Tag>
                      </>
                    )}
                    {position.priceStale && (
                      <span title="Stale price" className="warn">
                        {' '}
                        ⚠
                      </span>
                    )}
                  </td>
                  <td className="num">{formatQuantity(Math.abs(position.quantity))}</td>
                  <td className="num">£{position.averageCostGbp.toFixed(2)}</td>
                  <td className="num">
                    {position.currentPriceGbp === null ? '—' : `£${position.currentPriceGbp.toFixed(2)}`}
                  </td>
                  <td className="num">{formatGbp(position.marketValueGbp)}</td>
                  <td className="num">
                    <Money value={position.unrealisedPnlGbp} showSign />
                    <div className="mini">
                      <Percent value={position.unrealisedPnlPct} />
                    </div>
                  </td>
                  <td className="num">
                    {position.pctOfEquity === null ? '—' : `${position.pctOfEquity.toFixed(0)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {stream.lastCycle && (
        <details className="fold" style={{ marginTop: 12 }}>
          <summary>
            last cycle {formatRelative(stream.lastCycle.startedAt)} · {stream.lastCycle.status} ·{' '}
            {stream.lastCycle.ordersExecuted} filled
          </summary>
          <pre>{stream.lastCycle.note ?? stream.lastCycle.error ?? 'no detail recorded'}</pre>
        </details>
      )}
    </Panel>
  );
}
